import { useLayoutEffect } from 'react';

/**
 * Keeping absolutely placed text on its measured baseline.
 *
 * A form's metrics give each line a baseline, but neither renderer puts it there
 * from `top` alone, and each misses by a different amount:
 *
 * - Chrome rounds a face's ascent and descent to whole pixels when it lays out a
 *   line, so the baseline drifts inside the line box with the font size (up to
 *   ~0.9 pt at the sizes answers fit to).
 * - html2canvas draws at `range top + baseline`, where its baseline is read from
 *   `offsetTop` and so is a whole number — it wanders by the size too.
 *
 * So both are measured per line instead of corrected by one constant. An element
 * declares its target with `data-baseline` (in page points); `useBaselineSnap`
 * trues up the preview and `alignExportBaselines` the export clone.
 */

const measureBaselineWithin = (el) => {
    const marker = el.ownerDocument.createElement('i');
    marker.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
    el.appendChild(marker);
    // Rects carry the preview's scale; the computed height does not
    // (offsetHeight would, but rounded to a whole pixel).
    const box = el.getBoundingClientRect();
    const scale = box.height / parseFloat(getComputedStyle(el).height) || 1;
    const baseline = (marker.getBoundingClientRect().top - box.top) / scale;
    marker.remove();
    return baseline;
};

/**
 * @param {import('react').RefObject<HTMLElement>} ref A span with an inline `top`
 *   and font size and a `data-baseline` target.
 * @param {Array} deps Re-measure when these change.
 */
export function useBaselineSnap(ref, deps) {
    useLayoutEffect(() => {
        const snap = () => {
            const el = ref.current;
            if (!el || !el.getClientRects().length) return;
            el.style.marginTop = '0px';
            const target = parseFloat(el.dataset.baseline) - parseFloat(el.style.top);
            el.style.marginTop = `${target - measureBaselineWithin(el)}px`;
        };
        snap();
        // Measured against a fallback face the snap is wrong, and `fonts.ready`
        // may already have settled before Kalpurush was even requested — so wait
        // for Kalpurush itself at this size.
        let cancelled = false;
        const size = parseFloat(ref.current?.style.fontSize);
        if (size && document.fonts) {
            document.fonts.load(`${size}px Kalpurush`).then(() => {
                if (!cancelled) snap();
            }).catch(() => {});
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/** The baseline html2canvas will use: its own FontMetrics probe, step for step. */
function html2canvasBaseline(doc, family, fontSize) {
    const container = doc.createElement('div');
    const img = doc.createElement('img');
    const span = doc.createElement('span');
    container.style.cssText = 'visibility:hidden;margin:0;padding:0;white-space:nowrap';
    container.style.fontFamily = family;
    container.style.fontSize = fontSize;
    doc.body.appendChild(container);
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    img.width = 1;
    img.height = 1;
    img.style.cssText = 'margin:0;padding:0;vertical-align:baseline';
    span.style.cssText = 'margin:0;padding:0';
    span.style.fontFamily = family;
    span.style.fontSize = fontSize;
    span.appendChild(doc.createTextNode('Hidden Text'));
    container.appendChild(span);
    container.appendChild(img);
    const baseline = img.offsetTop - span.offsetTop + 2;
    doc.body.removeChild(container);
    return baseline;
}

/**
 * In html2canvas's `onclone`: move every `[data-baseline]` element so its text
 * is drawn on that baseline. Reads first and writes after, so one pass over a
 * full page costs a single layout.
 * @param {HTMLElement} root The page, in the cloned document.
 */
export function alignExportBaselines(root) {
    const doc = root.ownerDocument;
    const view = doc.defaultView;
    const lines = Array.from(root.querySelectorAll('[data-baseline]'));
    lines.forEach((el) => { el.style.marginTop = '0px'; });

    const rootTop = root.getBoundingClientRect().top;
    const cache = new Map();
    const moves = [];
    for (const el of lines) {
        const node = Array.from(el.childNodes).find((child) => child.nodeType === 3 && child.data.trim());
        if (!node) continue;
        const range = doc.createRange();
        range.selectNodeContents(node);
        const rect = range.getClientRects()[0];
        if (!rect) continue;
        const style = view.getComputedStyle(el);
        const key = `${style.fontSize}|${style.fontFamily}`;
        if (!cache.has(key)) cache.set(key, html2canvasBaseline(doc, style.fontFamily, style.fontSize));
        const drawn = rect.top - rootTop + cache.get(key);
        moves.push([el, parseFloat(el.dataset.baseline) - drawn]);
    }
    moves.forEach(([el, delta]) => {
        el.style.top = `${parseFloat(el.style.top) + delta}px`;
        // Moving the box up to land the text takes the bottom of a clipped box
        // with it, so the room under the baseline has to be given back or
        // descenders, commas and whole digits get cut off.
        el.style.height = `${parseFloat(el.style.fontSize) * 2.5}px`;
    });
}
